// Static-mode adapter: when REACT_APP_STATIC_MODE === "true", the SPA no longer
// talks to a FastAPI backend. GET requests are served from /public/data/*.json,
// the Contact form is POSTed to /api/contact.php (single PHP file on Simply),
// and non-migrated write endpoints resolve to a friendly no-op so pages that
// still render forms fail cleanly instead of throwing.
//
// The build stays 100% compatible with the SaaS mode: leave the env var unset
// (or "false") and axios keeps hitting REACT_APP_BACKEND_URL as before.

const DATA_BASE = (typeof window !== "undefined" ? window.location.origin : "") + "/data";
const NOOP_POST_ENDPOINTS = new Set([
  "/visit",
  "/quote",
  "/consultation",
  "/auth/forgot-password",
  "/auth/reset-password",
]);
const READ_MAP = {
  "/services":     "services",
  "/packages":     "packages",
  "/campaigns":    "campaigns",
  "/articles":     "articles",
  "/news":         "news",
  "/testimonials": "testimonials",
  "/team":         "team",
  "/policies":     "policies",
  "/sections":     "sections",
  "/assessments":  "assessments",
  "/site-settings": "site_settings",
  "/trainings":    "trainings",   // file may be absent → resolves to []
};

async function _readJson(name) {
  try {
    // cache: "no-store" — the /data/*.json files are the single source of
    // truth for the whole public site and are regenerated on every admin
    // save. If the browser (or any intermediary) cached them, admin edits
    // would appear stale on the frontend even though MySQL is up to date.
    const r = await fetch(`${DATA_BASE}/${name}.json`, {
      credentials: "omit",
      cache: "no-store",
    });
    if (!r.ok) return null;
    return await r.json();
  } catch { return null; }
}

async function _homepageStats() {
  const [services, sections, articles, testimonials, packages, campaigns] = await Promise.all([
    _readJson("services"), _readJson("sections"), _readJson("articles"),
    _readJson("testimonials"), _readJson("packages"), _readJson("campaigns"),
  ]);
  // Live counters from Simply — increments once per pageview + issues cookies
  // to distinguish visitors from sessions. Failure is silent (falls back to 0).
  let live = { visitors: 0, sessions: 0, page_views: 0, services_active: 0 };
  try {
    const r = await fetch("/api/activity.php", { credentials: "include", cache: "no-store" });
    if (r.ok) live = { ...live, ...(await r.json()) };
  } catch (_e) { /* keep zeros */ }

  const activeServices = (services || []).filter(
    (s) => s.is_active !== false && s.is_archived !== true,
  ).length;

  const heroValues = {
    hero_metric_visitors:  live.visitors,
    hero_metric_sessions:  live.sessions,
    hero_metric_pageviews: live.page_views,
    hero_metric_services:  live.services_active || activeServices,
  };
  const hero_metrics = (sections || [])
    .filter((s) => s.key && s.key.startsWith("hero_metric_") && s.enabled)
    .sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
    .map((s) => ({ key: s.key, name_ar: s.name_ar, name_en: s.name_en,
                   value: heroValues[s.key] ?? 0, order: s.order }));
  return {
    services_total: activeServices,
    articles_total: (articles || []).length,
    testimonials_total: (testimonials || []).length,
    packages_total: (packages || []).length,
    campaigns_active: (campaigns || []).length,
    hero_metrics,
  };
}

// Custom axios adapter (installed only when static mode is on).
export async function staticAdapter(config) {
  const method = (config.method || "get").toLowerCase();
  const url = (config.url || "").replace(/^\/+/, "/");

  // Contact form → real PHP handler on Simply.
  if (method === "post" && url === "/contact") {
    const r = await fetch("/api/contact.php", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: config.data,
      credentials: "omit",
    });
    const data = await r.json().catch(() => ({}));
    if (!r.ok) {
      const err = new Error(data?.detail || r.statusText);
      err.response = { status: r.status, data };
      throw err;
    }
    return { data, status: r.status, statusText: r.statusText, headers: {}, config };
  }

  // Data Subject Rights form → real PHP handler on Simply.
  if (method === "post" && url === "/dsr") {
    const r = await fetch("/api/dsr.php", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: config.data,
      credentials: "omit",
    });
    const data = await r.json().catch(() => ({}));
    if (!r.ok) {
      const err = new Error(data?.detail || r.statusText);
      err.response = { status: r.status, data };
      throw err;
    }
    return { data, status: r.status, statusText: r.statusText, headers: {}, config };
  }

  // Service Quote Request → real PHP handler on Simply.
  if (method === "post" && url === "/quote") {
    const r = await fetch("/api/quote.php", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: config.data,
      credentials: "omit",
    });
    const data = await r.json().catch(() => ({}));
    if (!r.ok) {
      const err = new Error(data?.detail || r.statusText);
      err.response = { status: r.status, data };
      throw err;
    }
    return { data, status: r.status, statusText: r.statusText, headers: {}, config };
  }

  // Known write endpoints not migrated to static → no-op that pages can display.
  if (method === "post" && NOOP_POST_ENDPOINTS.has(url)) {
    return { data: { ok: true, static_mode: true }, status: 200, statusText: "OK", headers: {}, config };
  }

  // GET a policy by key
  if (method === "get" && /^\/policies\/[^/]+$/.test(url)) {
    const key = url.split("/").pop();
    const all = (await _readJson("policies")) || [];
    const found = all.find((p) => p.key === key);
    if (!found) {
      const err = new Error("Not found");
      err.response = { status: 404, data: { detail: "Not found" } };
      throw err;
    }
    return { data: found, status: 200, statusText: "OK", headers: {}, config };
  }

  // Derived stats
  if (method === "get" && url === "/homepage-stats") {
    return { data: await _homepageStats(), status: 200, statusText: "OK", headers: {}, config };
  }

  // Static file map
  if (method === "get" && READ_MAP[url]) {
    const data = (await _readJson(READ_MAP[url])) ?? [];
    return { data, status: 200, statusText: "OK", headers: {}, config };
  }

  // Everything else → 404 (auth, admin, analytics, assessment, visits)
  const err = new Error(`static-mode: endpoint not migrated (${method.toUpperCase()} ${url})`);
  err.response = { status: 404, data: { detail: "Endpoint not available in static mode" } };
  throw err;
}

export const STATIC_MODE = process.env.REACT_APP_STATIC_MODE === "true";
